/**
 * Type-safe event definitions as a discriminated union.
 *
 * Each event name maps to a typed property payload.
 * Adding a new event here provides compile-time safety and IDE autocomplete.
 *
 * Routing: 'ga4' = pushed to dataLayer for GTM/GA4 tags,
 *          'posthog' = captured via PostHog SDK,
 *          'both' = both destinations.
 */

import type { FeatureArea } from './feature-areas';

// ── Registration & Onboarding ────────────────────────────────────────

export type RegistrationEvent =
  | {
      name: 'signup_started';
      properties: {
        source_page: string;
        cta_location?: string;
        /** W1-0 (REK-2146): how the user authenticates on this path. */
        auth_method?: 'email_otp' | 'phone_otp' | 'oauth_google' | 'oauth_facebook';
        /**
         * W1-0 (REK-2146): map of registered experiment key → assigned variant.
         * Schema YAML types this as string (primitive-only validator); runtime emits an object.
         */
        experiment_variants?: Record<string, string>;
        /** W1-0 (REK-2146): utm/blob source, else ref, else "direct". */
        entry_source?: string;
      };
    }
  | { name: 'registration_completed'; properties: { method: string; referral_code?: string; country?: string } }
  | {
      name: 'signup_completed';
      properties: {
        origin?: string;
        time_to_complete?: number;
        auth_method?: 'email_otp' | 'phone_otp' | 'oauth_google' | 'oauth_facebook';
        experiment_variants?: Record<string, string>;
        entry_source?: string;
      };
    }
  | { name: 'investor_marketing_opt_in'; properties: { source: string; already_subscribed: boolean } }
  | { name: 'unveil_marketing_opt_in'; properties: { source_page?: string } }
  | { name: 'email_verified'; properties: Record<string, never> }
  | { name: 'phone_verified'; properties: Record<string, never> }
  | { name: 'onboarding_step_completed'; properties: { step_name: string; step_index: number } }
  | { name: 'onboarding_completed'; properties: { time_to_complete?: number; profile_completed_pct?: number } }
  | { name: 'profile_setup_completed'; properties: Record<string, never> }
  | { name: 'profile_field_completed'; properties: { field_name: string; step_name: string; step_index: number } }
  | { name: 'profile_wizard_opened'; properties: { completion_pct: number; completed_steps: number; total_steps: number } }
  | { name: 'profile_wizard_abandoned'; properties: { completion_pct: number; completed_steps: number; total_steps: number; time_spent_ms?: number } }
  | { name: 'exposure_scan_started'; properties: Record<string, never> }
  | { name: 'exposure_scan_completed'; properties: { brokers_found: number; breaches_found: number; scan_duration?: number } };

// ── Legacy migration (Intercom / legacy app → RWA) ───────────────────

export type MigrationEvent =
  | {
      name: 'migration_campaign_landed';
      properties: {
        utm_source?: string;
        utm_medium?: string;
        utm_campaign?: string;
        utm_content?: string;
        utm_term?: string;
        auth_mode?: string;
      };
    }
  | {
      name: 'migration_campaign_attribution';
      properties: {
        utm_source?: string;
        utm_medium?: string;
        utm_campaign?: string;
        utm_content?: string;
        utm_term?: string;
      };
    }
  | {
      name: 'migration_completed';
      properties: {
        migrated: boolean;
        utm_source?: string;
        utm_medium?: string;
        utm_campaign?: string;
        utm_content?: string;
        utm_term?: string;
      };
    }
  | {
      name: 'migration_bonus_awarded';
      properties: {
        bonus_points: number;
        utm_campaign: string;
        source: 'backfill' | 'realtime';
      };
    };

// ── Protect ──────────────────────────────────────────────────────────

export type ProtectEvent =
  | { name: 'protect_page_viewed'; properties: Record<string, never> }
  | { name: 'protect_scan_initiated'; properties: Record<string, never> }
  | { name: 'protect_scan_completed'; properties: { brokers_found: number; brokers_scanned: number } }
  | { name: 'protect_broker_found'; properties: { broker_name: string; broker_type?: string } }
  | { name: 'protect_removal_requested'; properties: { broker_name: string } }
  | { name: 'protect_removal_confirmed'; properties: { broker_name: string; days_to_remove?: number } }
  | { name: 'protect_dashboard_viewed'; properties: Record<string, never> }
  | { name: 'protect_alert_clicked'; properties: { alert_type: string } }
  | { name: 'pricing_viewed'; properties: { plan_type?: string } }
  | { name: 'checkout_started'; properties: { plan_type: string; price: number } };

// ── Subscriptions ────────────────────────────────────────────────────

export type SubscriptionEvent =
  | { name: 'subscription_created'; properties: { plan_type: string; price: number; trial: boolean } }
  | { name: 'subscription_cancelled'; properties: { reason?: string; subscription_age_days?: number } }
  | { name: 'subscription_renewed'; properties: Record<string, never> };

// ── Surveys ──────────────────────────────────────────────────────────

export type SurveyEvent =
  | { name: 'survey_offered'; properties: { survey_id: string; survey_category?: string; estimated_minutes?: number } }
  | { name: 'survey_started'; properties: { survey_id: string } }
  | { name: 'survey_question_answered'; properties: { survey_id: string; question_index: number; question_type?: string; time_on_question_ms?: number } }
  | { name: 'survey_completed'; properties: { survey_id: string; entries_earned: number; completion_time_ms?: number } }
  | { name: 'survey_abandoned'; properties: { survey_id: string; last_question_index: number; reason?: string } }
  | { name: 'survey_reward_claimed'; properties: { survey_id: string; reward_amount: number; reward_type?: string } }
  | { name: 'survey_dashboard_viewed'; properties: { surveys_presented: number; surveys_completed: number } }
  | {
      name: 'survey_next_hop';
      properties: { entry: 'sidebar' | 'route' | 'deeplink' | 'cta' | 'notification' };
    }
  | {
      name: 'survey_next_hop_skipped';
      properties: { reason: 'loop_guard' | 'env' | 'storage' };
    }
  | { name: 'campaign_started'; properties: { campaign_id: string } }
  | {
      name: 'campaign_completed';
      properties: {
        campaign_id: string;
        reward: number;
        targeting_miss?: boolean;
        award_status?: 'granted' | 'pending' | 'failed' | 'skipped';
      };
    };

// ── Rewards & Jackpot ────────────────────────────────────────────────

export type RewardsEvent =
  | { name: 'reward_earned'; properties: { reward_type: string; reward_amount: number; source_feature: string; recurring_vs_onetime?: string } }
  | { name: 'reward_redeemed'; properties: { reward_type: string; reward_amount: number; redemption_method?: string } }
  | { name: 'points_balance_viewed'; properties: { available_points: number; total_committed?: number } }
  | { name: 'reward_milestone_reached'; properties: { milestone_name: string; milestone_value: number } }
  | { name: 'paycheck_available_shown'; properties: { pending_points: number; campaign_count: number; source: 'overview' | 'surveys' | 'wallet' } }
  | {
      name: 'paycheck_claimed';
      properties:
        | { total_points: number; campaigns_claimed: number; new_balance: number; source: 'overview' | 'surveys' | 'wallet' }
        | { total_points: number; campaigns_claimed: number; new_balance: number; channel: 'sms' };
    }
  | {
      name: 'achievement_completed';
      properties: {
        achievement_id: string;
        achievement_name: string;
        /**
         * Social Community defs: `ACHIEVEMENT_DEFINITIONS.type` (e.g. verified, platforms).
         * Platform `user_achievements`: use `platform` (see `achievement_tier`).
         */
        achievement_type: string;
        achievement_source?: 'social_gamification' | 'platform';
        achievement_tier?: string;
      };
    }
  | { name: 'jackpot_viewed'; properties: { jackpot_type: string; prize_amount?: number } }
  | {
      name: 'jackpot_entry_submitted';
      properties: {
        jackpot_type: string;
        entries_count: number;
        points_used: number;
        source?: 'wallet' | 'panel_complete';
      };
    }
  | { name: 'jackpot_autopledge_enabled'; properties: Record<string, never> }
  | { name: 'jackpot_autopledge_disabled'; properties: Record<string, never> }
  | { name: 'jackpot_result_viewed'; properties: { jackpot_type: string; won: boolean; prize_amount?: number } }
  | { name: 'jackpot_preference_chosen'; properties: { preference: string; source: string } }
  | { name: 'jackpot_preference_changed'; properties: { from_preference: string; to_preference: string; source: string } }
  | { name: 'jackpot_auto_entry_configured'; properties: { draw_type: string; amount: number; amount_type: string; is_initial_setup: boolean } }
  | {
      name: 'pool_next_hop';
      properties: { entry: 'route' | 'sidebar' | 'earn' };
    }
  | {
      name: 'pool_next_hop_skipped';
      properties: { reason: 'loop_guard' | 'env' | 'storage' };
    };

// ── Referrals ────────────────────────────────────────────────────────

export type ReferralEvent =
  | { name: 'referral_page_viewed'; properties: { referral_count: number; total_points_earned: number } }
  | { name: 'referral_link_copied'; properties: { referral_code: string; source: string } }
  | { name: 'referral_shared'; properties: { platform: string } }
  | { name: 'referral_link_clicked'; properties: { platform?: string } }
  | { name: 'referral_signup_completed'; properties: { referring_user_id: string } }
  | { name: 'referral_milestone_reached'; properties: { milestone: number; total_referrals: number } }
  | { name: 'referral_reward_earned'; properties: { referral_count: number; points_earned: number } }
  | { name: 'referral_reward_granted'; properties: { reward_type: string; points_amount: number } };

// ── S4R (Shares for Reklaim) ────────────────────────────────────────

export type S4REvent =
  | { name: 's4r_disclosure_signed'; properties: { disclosure_version: string } }
  | { name: 's4r_pledge_submitted'; properties: { dollar_value: number; estimated_shares?: number } }
  | { name: 's4r_pledge_modified'; properties: { dollar_value: number } }
  | { name: 's4r_pledge_cancelled'; properties: Record<string, never> };

// ── Email Management ────────────────────────────────────────────────

export type EmailManagementEvent =
  | { name: 'email_added'; properties: Record<string, never> }
  | { name: 'email_removed'; properties: Record<string, never> }
  | { name: 'email_primary_changed'; properties: Record<string, never> };

// ── Feed & Content ───────────────────────────────────────────────────

export type FeedEvent =
  | { name: 'feed_viewed'; properties: Record<string, never> }
  | { name: 'feed_item_viewed'; properties: { item_id: string; item_type: string } }
  | { name: 'feed_item_liked'; properties: { item_id: string } }
  | { name: 'feed_item_shared'; properties: { item_id: string; share_channel: string } }
  | { name: 'feed_item_clicked'; properties: { item_id: string; click_target: string } }
  | {
      name: 'social_promotion_submitted';
      properties: {
        platform: string;
        post_type: string;
        promotion_id: string;
        draw_id?: string | null;
      };
    };

// ── Brand (B2B) ─────────────────────────────────────────────────────

export type BrandEvent =
  | { name: 'brand_registered'; properties: Record<string, never> }
  | { name: 'brand_setup_completed'; properties: Record<string, never> }
  | { name: 'brand_dashboard_viewed'; properties: Record<string, never> }
  | { name: 'campaign_created'; properties: { campaign_type?: string } }
  | { name: 'campaign_launched'; properties: { campaign_id: string; target_audience_size?: number } }
  | { name: 'campaign_paused'; properties: { campaign_id: string } }
  | { name: 'brand_report_downloaded'; properties: { report_type: string } }
  // REK-1729: client-side breadcrumb fired from /contact-sales when the
  // optional marketing-opt-in checkbox is ticked at submit time. Pairs
  // with the server-side Braze `/users/track` write (alias =
  // prelogin_email, is_b2b: true, subscribed to the B2B group). The
  // event itself is PostHog only; CRM is owned by the edge function.
  | {
      name: 'sales_marketing_opt_in';
      properties: {
        source_page: string;
        has_company?: boolean;
        has_title?: boolean;
      };
    };

// ── Cross-Feature & Engagement ───────────────────────────────────────

export type EngagementEvent =
  | { name: 'session_started'; properties: { device?: string; browser?: string; platform?: string } }
  | { name: 'feature_engaged'; properties: { feature_area: FeatureArea } }
  | { name: 'cross_sell_prompt_shown'; properties: { source_feature: FeatureArea; target_feature: FeatureArea } }
  | { name: 'cross_sell_prompt_clicked'; properties: { source_feature: FeatureArea; target_feature: FeatureArea } }
  | { name: 'consent_decision'; properties: { decision: 'accept_all' | 'analytics_only' | 'reject_non_essential' | 'customize'; variant: string; time_to_decision_ms: number; trigger?: 'banner' | 'signup_implied' } }
  | { name: 'ruby_sms_opt_in'; properties: Record<string, never> };

// ── Experiment ───────────────────────────────────────────────────────

export type ExperimentEvent =
  | { name: 'experiment_viewed'; properties: { experiment_key: string; variant: string } }
  | { name: 'feature_flag_evaluated'; properties: { flag_key: string; flag_value: string } };

// ── Auth ─────────────────────────────────────────────────────────────

/**
 * REK-2030: which fail-closed branch of ClerkAuthProvider's legacy-identity
 * resolution denied the session. Kept in lock-step with the `reason` enum on
 * the `login_failed` event in analytics/schema/events.yaml — a tuple rather
 * than a bare union so the values are enumerable at runtime and the two lists
 * can be diffed instead of eyeballed (Copilot on PR #737).
 */
export const LOGIN_FAILED_REASONS = [
  'unprovisioned',
  'poll_timeout',
  'lookup_error',
  'locked',
  'hydration_timeout',
] as const;

export type LoginFailedReason = (typeof LOGIN_FAILED_REASONS)[number];

export type AuthEvent =
  | { name: 'login'; properties: { method: string } }
  | { name: 'login_failed'; properties: { reason: LoginFailedReason } }
  | { name: 'logout'; properties: Record<string, never> };

// ── Errors ───────────────────────────────────────────────────────────

export type ErrorEvent =
  | { name: 'error_occurred'; properties: { error_type: string; message: string; stack?: string } };

// ── Facebook Lead Ad → User Account funnel (REK-1469) ───────────────

export type FbLeadEvent =
  | {
      name: 'fb_lead_email_validation_passed';
      properties: {
        lead_id: string;
        form_id?: string;
        platform?: string;
        /** Original Meta surface pre-normalisation (REK-1590). */
        source_surface?: string;
        /** NeverBounce verification result (always "valid" for the passed event). */
        nb_result?: string | null;
      };
    }
  | {
      name: 'fb_lead_email_validation_failed';
      properties: {
        lead_id: string;
        form_id?: string;
        platform?: string;
        /** Original Meta surface pre-normalisation (REK-1590). */
        source_surface?: string;
        /** NeverBounce result (`invalid`/`disposable`/`catchall`/`unknown`) or null on operational error. */
        nb_result?: string | null;
        /** True when the failure was operational (missing key, network error, non-2xx) vs a clean reject. */
        nb_api_error?: boolean;
        /** Short machine-friendly reason; never PII. */
        nb_reason?: string | null;
      };
    }
  | {
      name: 'fb_lead_received';
      properties: {
        lead_id: string;
        form_id: string;
        platform: string;
        /**
         * Original Meta surface (`fb`, `ig`, `facebook`, `instagram`) before
         * alias normalisation. REK-1590 -- lets the funnel dashboard split by
         * surface without a schema migration. Optional for backward compat
         * with any historical event already in PostHog.
         */
        source_surface?: string;
        ad_id?: string | null;
        ad_name?: string | null;
        adset_id?: string | null;
        adset_name?: string | null;
        campaign_id?: string | null;
        campaign_name?: string | null;
        lead_created_time?: string | null;
      };
    }
  | {
      name: 'fb_lead_account_created';
      properties: {
        user_id: string;
        lead_id: string;
        is_existing_user: boolean;
        /** Only set on the new-user branch; existing-email branch never awards points. */
        points_awarded?: boolean;
        form_id: string;
        platform?: string;
        /** Original Meta surface pre-normalisation (REK-1590). */
        source_surface?: string;
        ad_id?: string | null;
        ad_name?: string | null;
        adset_id?: string | null;
        adset_name?: string | null;
        campaign_id?: string | null;
        campaign_name?: string | null;
        lead_created_time?: string | null;
      };
    }
  | {
      name: 'fb_lead_welcome_email_sent';
      properties: {
        user_id: string;
        lead_id: string;
        provider: string;
        template: string;
        /** Original Meta surface pre-normalisation (REK-1590). */
        source_surface?: string;
      };
    }
  | {
      name: 'fb_lead_welcome_email_clicked';
      properties: {
        utm_source?: string;
        utm_medium?: string;
        utm_campaign?: string;
        utm_content?: string;
        lead_id?: string;
      };
    }
  | {
      name: 'fb_lead_first_dashboard_view';
      properties: {
        lead_id?: string;
        time_since_lead_received_ms?: number;
      };
    }
  | {
      name: 'fb_lead_welcome_email_opened';
      properties: {
        template_name: 'fb_lead_welcome_v1';
        source?: string;
      };
    };

// ── Email open tracking (REK-1493) ───────────────────────────────────
//
// Fired server-side from `supabase/functions/track-email-open/index.ts`
// (loaded as a 1x1 pixel by recipient email clients). The TypeScript union
// + EVENT_ROUTING entry exist purely for schema/ESLint parity -- the SDK
// `analytics.trackEvent()` call site is server-side, not in React.
export type EmailEvent =
  | {
      name: 'email_opened';
      properties: {
        template_name: string;
        is_apple_mpp_likely?: boolean;
        source?: string;
      };
    };

// ── Braze one-tap login (REK-1591) ───────────────────────────────────
//
// All three events are fired server-side from the
// `issue-one-tap-token` / `redeem-one-tap-token` edge functions. The union
// + EVENT_ROUTING entries exist for schema/ESLint parity; client code does
// NOT call analytics.trackEvent() with these names.
export type MagicLinkEvent =
  | {
      name: 'magic_link_issued';
      properties: {
        user_id: string;
        source: string;
        ttl_seconds: number;
        redirect_to: string;
        has_idempotency_key: boolean;
      };
    }
  | {
      name: 'magic_link_redeemed';
      properties: {
        user_id: string;
        source: string;
        redirect_to: string;
        /**
         * True when this redemption re-minted a link for a token already used
         * within the grace window (an email scanner pre-spent it); false on the
         * initial claim. REK-1591 one-click hardening.
         */
        grace: boolean;
      };
    }
  | {
      name: 'magic_link_redemption_failed';
      properties: {
        /** One of `not_found`, `already_used`, `expired`, `unknown`. */
        reason: 'not_found' | 'already_used' | 'expired' | 'unknown';
        user_id?: string | null;
        source?: string | null;
      };
    };

// ── One-click (1.5-click) sign-up (REK-1730) ─────────────────────────
//
// Sign-up counterpart to MagicLinkEvent. All three events are fired
// server-side from the `issue-signup-token` / `redeem-signup-token` edge
// functions. The union + EVENT_ROUTING entries exist for schema/ESLint parity;
// client code does NOT call analytics.trackEvent() with these names.
export type SignupLinkEvent =
  | {
      name: 'signup_link_issued';
      properties: {
        source: string;
        ttl_seconds: number;
        redirect_to: string;
        has_idempotency_key: boolean;
        /** True when NB skipped for allowlisted source (ipost). REK-2013. */
        neverbounce_skipped?: boolean;
      };
    }
  | {
      name: 'signup_link_redeemed';
      properties: {
        source: string;
        redirect_to: string;
        /**
         * True when this redemption re-minted a link for a token already used
         * within the grace window; false on the initial claim. REK-1730.
         */
        grace: boolean;
        /**
         * True when confirm created a brand-new account (ToS consent recorded,
         * NO points bonus); false when the email already had an account and
         * confirm just logged them in.
         */
        account_created: boolean;
      };
    }
  | {
      name: 'signup_link_redemption_failed';
      properties: {
        /**
         * One of `email_validation_failed` (issue-time NeverBounce reject),
         * `not_found`, `already_used`, `expired`, `unknown` (redeem-time).
         */
        reason: 'email_validation_failed' | 'not_found' | 'already_used' | 'expired' | 'unknown';
        source?: string | null;
        nb_result?: string | null;
        nb_api_error?: boolean | null;
      };
    };

// ── Legacy compat (to be removed in Phase 5 cleanup) ─────────────────

export type LegacyEvent =
  | { name: 'page_view'; properties: { page?: string; page_path?: string; page_title?: string } }
  | { name: 'profile_updated'; properties: { updated_fields?: string } }
  | { name: 'prediction_submitted'; properties: { prediction_id: string; option_id: string } }
  | { name: 'cta_click'; properties: { cta_location: string; cta_text?: string } };

// ── Union ────────────────────────────────────────────────────────────

export type AnalyticsEvent =
  | RegistrationEvent
  | MigrationEvent
  | ProtectEvent
  | SubscriptionEvent
  | SurveyEvent
  | RewardsEvent
  | ReferralEvent
  | S4REvent
  | EmailManagementEvent
  | FeedEvent
  | BrandEvent
  | EngagementEvent
  | ExperimentEvent
  | AuthEvent
  | ErrorEvent
  | FbLeadEvent
  | EmailEvent
  | MagicLinkEvent
  | SignupLinkEvent
  | LegacyEvent;

export type AnalyticsEventName = AnalyticsEvent['name'];

/**
 * Routing rules: which events go to GA4, PostHog, and/or Braze.
 * Events not listed default to 'posthog' only.
 *
 * 'all'           = GA4 + PostHog + Braze
 * 'posthog_braze' = PostHog + Braze (no GA4)
 * 'both'          = GA4 + PostHog (no Braze) — legacy alias
 * 'ga4'           = GA4 only
 * 'posthog'       = PostHog only (default)
 * 'braze'         = Braze only
 */
export const EVENT_ROUTING: Partial<Record<AnalyticsEventName, 'ga4' | 'posthog' | 'both' | 'braze' | 'posthog_braze' | 'all'>> = {
  // GA4 + PostHog + Braze (login has ga4 in schema)
  login: 'all',
  // REK-2030: the auth-failure counterpart to `login`. PostHog only — failure
  // noise has no place in Braze lifecycle or GA4 conversion reporting, matching
  // the other *_failed events below.
  login_failed: 'posthog',

  migration_campaign_landed: 'all',
  migration_campaign_attribution: 'all',
  migration_completed: 'all',
  migration_bonus_awarded: 'posthog',

  // PostHog + Braze (schema has no ga4 for these)
  registration_completed: 'posthog_braze',
  // REK-1368: server-side emit from braze-sms-inbound on SMS YES opt-in;
  // union + routing exist for schema/ESLint parity (not client SDK).
  phone_verified: 'posthog_braze',
  logout: 'posthog_braze',
  signup_completed: 'posthog_braze',
  subscription_created: 'posthog_braze',
  subscription_cancelled: 'posthog_braze',
  subscription_renewed: 'posthog_braze',

  // PostHog + Braze (lifecycle events for Canvas triggers)
  onboarding_completed: 'posthog_braze',
  onboarding_step_completed: 'posthog_braze',
  profile_setup_completed: 'all',
  profile_wizard_opened: 'posthog_braze',
  profile_wizard_abandoned: 'posthog_braze',
  survey_started: 'posthog_braze',
  // Vite → Next survey entry hop. PostHog only; the surface does not port.
  survey_next_hop: 'posthog',
  survey_next_hop_skipped: 'posthog',
  // Vite → Next pool entry hop. PostHog only.
  pool_next_hop: 'posthog',
  pool_next_hop_skipped: 'posthog',
  survey_completed: 'all',
  survey_abandoned: 'posthog_braze',
  campaign_started: 'posthog_braze',
  reward_earned: 'posthog_braze',
  // Schema includes ga4 — keep full funnel + Braze
  reward_milestone_reached: 'all',
  // REK-1380: paycheck claim lifecycle — Braze canvases trigger SMS + email
  paycheck_available_shown: 'posthog_braze',
  paycheck_claimed: 'posthog_braze',
  jackpot_entry_submitted: 'posthog_braze',
  referral_shared: 'all',
  referral_signup_completed: 'posthog_braze',
  social_promotion_submitted: 'posthog_braze',
  s4r_pledge_submitted: 'posthog_braze',
  s4r_pledge_cancelled: 'posthog_braze',
  s4r_disclosure_signed: 'posthog_braze',
  // REK-1366: Braze Canvas trigger for Ruby welcome SMS (client SDK only)
  ruby_sms_opt_in: 'braze',

  // GA4 + PostHog (no Braze)
  page_view: 'both',
  consent_decision: 'both',

  // GA4 only (marketing/acquisition)
  cta_click: 'ga4',
  referral_link_clicked: 'ga4',

  // REK-1728: Investor newsletter consent. PostHog only — server-side
  // newsletter-subscribe edge function owns the Braze profile write
  // (alias-keyed prelogin_email + is_investor attribute), so this
  // client-side event does NOT need to route to Braze.
  investor_marketing_opt_in: 'posthog',

  // REK-1469: Facebook Lead Ad funnel.
  // Server-side events (`fb_lead_email_validation_passed`,
  // `fb_lead_email_validation_failed`, `fb_lead_received`,
  // `fb_lead_account_created`, `fb_lead_welcome_email_sent`) are emitted by
  // fb-lead-ingest via PostHog's HTTP capture endpoint — they are NOT fired
  // through the client SDK's `analytics.trackEvent()`. Routing entries below
  // document the intended sink(s) and keep schema/ESLint parity; if a client
  // call site ever uses these names, routing follows the same rules.
  // Client-side funnel events (`fb_lead_welcome_email_clicked`,
  // `fb_lead_first_dashboard_view`) DO pass through the SDK and use the
  // routing values below at runtime.
  fb_lead_email_validation_passed: 'posthog',
  fb_lead_email_validation_failed: 'posthog',
  fb_lead_received: 'posthog',
  fb_lead_account_created: 'posthog_braze',
  fb_lead_welcome_email_sent: 'posthog',
  fb_lead_welcome_email_clicked: 'both',
  fb_lead_first_dashboard_view: 'both',

  // REK-1493: Server-side email open tracking. Both events are emitted by
  // the `track-email-open` edge function via `postHogCapture()` and never
  // pass through this routing map — listed here for schema/ESLint parity
  // and documentation only. PostHog only (matches `fb_lead_welcome_email_sent`);
  // server-side GA4 is not part of our stack.
  email_opened: 'posthog',
  fb_lead_welcome_email_opened: 'posthog',

  // REK-1591: Braze one-tap login. Server-side only; listed for schema parity.
  magic_link_issued: 'posthog',
  magic_link_redeemed: 'posthog',
  magic_link_redemption_failed: 'posthog',

  // REK-1730: one-click (1.5-click) sign-up. Server-side only; schema parity.
  signup_link_issued: 'posthog',
  signup_link_redeemed: 'posthog',
  signup_link_redemption_failed: 'posthog',

  // REK-1727: Unveil marketing-consent funnel. Client-side capture only —
  // PostHog. The downstream Braze profile creation + `unveil_start` event
  // are server-side in the `unveil-braze` edge function (not routed through
  // this map; the function calls /users/track directly).
  unveil_marketing_opt_in: 'posthog',

  // REK-1729: sales-form marketing-opt-in. PostHog only; the Braze tag
  // is performed server-side from the contact-sales edge function as
  // an additive `/users/track` (alias-keyed) -- this event is the
  // client-visible funnel breadcrumb, not a Braze SDK call.
  sales_marketing_opt_in: 'posthog',

  // Everything else defaults to PostHog only
};
